import { CancelTokenSource } from 'axios';
import * as d3 from 'd3-scale-chromatic';

// Only seen box so far. If more exist, add as union here.
type AnnotationType = 'box';

// Only seen custom so far. If more exist, add as union here.
type AnnotationListType = 'custom';

// TODO: Temporary until can be imported from vue-media-annotator
export type RectBounds = [number, number, number, number];

export interface Annotation {
  coordinates: [number, number, number, number];
  type: AnnotationType;
  class: string;
  model_score?: number;
}

export interface DatasetListing {
  _id: string;
  count: number;
  flagged: number;
  confirmed: number;
  granted_institutions: string[];
  subject_type: string;
  created_at: string | null;
  validation_only: boolean;
}

export interface DatasetListingResponse {
  total_number: number;
  total_pages: number;
  limit: number;
  page: number;
  datasets_meta: DatasetListing[];
}

export interface FullDatasetListing extends DatasetListing {
  subject_type: string;
  confirmed: number;
}

export interface DatasetGenerationRequest {
  video: string;
  params: {
    dataset_id: string;
    study_type: string;
    number_of_samples: number;
  };
}

export interface DatasetGenerationResponse {
  type: 'success' | 'error';
  status_code: number;
  message: {
    dataset_id: string;
    id_list: string[];
  } | string;
}

export interface DatasetGenerationSuccess extends DatasetGenerationResponse {
  type: 'success';
  status_code: 200;
  message: {
    dataset_id: string;
    id_list: string[];
  };
}

export interface GenericResponse {
  type: 'success' | 'error';
  message: string;
  status_code: number;
}

export interface DatasetDeletionResponse {
  type: 'success';
  status_code: 200;
  result: number;
}

export interface FauModelStatisticsResults {
  accuracy: number;
  f1?: number;
  precision?: number;
  sensitivity?: number;
}

export interface FauModelStatistics {
  results: FauModelStatisticsResults;
  test_confusion_matrix: number[][];
}

export interface ModelStatistics {
  ears: FauModelStatistics;
  orbital: FauModelStatistics;
  nose: FauModelStatistics;
  whiskers: FauModelStatistics;
  cheek: FauModelStatistics;
}

export interface ModelStatisticsResponse {
  subject_type: string;
  study_type: string;
  model_id: string;
  statistics: ModelStatistics;
}

export type StudyType = 'general' | 'pain-mgs' | 'fau';
export type Models = Record<string, Record<StudyType, string[]>>;

export interface VideoList {
  _id: string;
  name: string;
  owner?: string;
  path: string;
  number_of_frames: number;
  frame_per_second: number;
  duration: number;
  subject_type: string;
  subject_sex?: string;
  data_source: string | null;
  granted_institutions: string[];

  // Often not present
  framelayout?: number[];
  details?: string[];
  remarks?: string;

}

export interface VideoResponse {
  total_number: number;
  total_pages: number;
  limit: number;
  page: number;
  videos: VideoList[];
}

export interface VideoUploadMetadata {
  subject_type: string;
  subject_sex: string;
  data_source: string;
  details?: string[];
  remarks?: string;
}

export interface VideoUploadParams {
  file: File;
  meta: VideoUploadMetadata;
}

export interface Upload extends VideoUploadParams {
  progress: number;
  error: string | null;
  finished: boolean;
  cancelled: boolean;
  cancelToken: CancelTokenSource;
  onProgress: (val: ProgressEvent) => unknown;
}

// This is the subset of the `reference` field of a dataset frame thats applies to the entire dataset
export interface DatasetMetadata {
  fps: number;
  number_of_frames: number;
  data_source: string;
  subject_type: string;
  study_type: string;

  // Video
  video_filename: string;
  video_storage?: 'system' | 'user';
}

export interface AnnotationFrameReference extends DatasetMetadata {
  date: string;
  camera: string;
  frame_index: number;
  time_in_seconds: number;
  scorer: string;
}

export interface NewAnnotationFrameReference {
  frame_index: number;
}

export interface StudyPainScore {
  [scoreClass: string]: number;
}
export type MotionActionMap = {'awake': boolean; 'labored_breathing': boolean; 'rearing': boolean; 'sniffing': boolean; 'grooming': boolean; 'dead': boolean} | undefined | null;
export type StudyMotionScore = {'start_frame': number; 'end_frame': number; 'actions': MotionActionMap};
export interface AnnotationScore {
  'pain-mgs'?: StudyPainScore | undefined;
  'pain-binary'?: StudyPainScore | undefined;
  'motion'?: StudyMotionScore | undefined;
}
export interface MotionAnnotationScore {
  'motion': StudyMotionScore | undefined;
}

export type AnnotationFrameStatus = 'confirmed' | 'pending' | 'flagged';

// Motion
export type RawMotionScoreValue = 0 | 1 | 2 | 3 | 4 | 5 | 6;
export type MotionScoreValue = RawMotionScoreValue | undefined;
export type MotionScoreValueText = 'Sleeping' | 'Awake' | 'Labored' | 'Rearing' | 'Sniffing' | 'Grooming' | 'Dead' | 'N/A';
export function MotionScoreValueToText(score: MotionScoreValue): MotionScoreValueText {
  switch (score) {
    case 0:
      return 'Sleeping';
    case 1:
      return 'Awake';
    case 2:
      return 'Labored';
    case 3:
      return 'Rearing';
    case 4:
      return 'Sniffing';
    case 5:
      return 'Grooming';
    case 6:
      return 'Dead';
    default:
      return 'N/A';
  }
}
export function MotionScoreValueToColor(score: MotionScoreValue) {
  const text = MotionScoreValueToText(score);
  switch (text) {
    case 'Sleeping':
      return d3.schemeCategory10[0];
    case 'Labored':
      return d3.schemeCategory10[1];
    case 'Awake':
      return d3.schemeCategory10[2];
    case 'Rearing':
      return d3.schemeCategory10[3];
    case 'Sniffing':
      return d3.schemeCategory10[4];
    case 'Grooming':
      return d3.schemeCategory10[5];
    case 'Dead':
      return 'black';
    default:
      return 'lightgray';
  }
}

export function MotionScoreMapToNumber(scoreBooleanMap: MotionActionMap | undefined | number): MotionScoreValue {
  if (scoreBooleanMap === undefined || scoreBooleanMap === null || typeof scoreBooleanMap === 'number') {
    return undefined;
  }
  if (scoreBooleanMap?.awake === false) {
    if (scoreBooleanMap?.dead) {
      return 6;
    }
    return 0;
  }
  if (scoreBooleanMap?.labored_breathing) {
    return 2;
  }
  if (scoreBooleanMap?.rearing) {
    return 3;
  }
  if (scoreBooleanMap?.sniffing) {
    return 4;
  }
  if (scoreBooleanMap?.grooming) {
    return 5;
  }
  if (scoreBooleanMap?.awake) {
    return 1;
  }
  return undefined;
}

export function MotionScoreNumberToMap(score: MotionScoreValue): MotionActionMap | undefined {
  if (score === undefined) {
    return undefined;
  }
  const scoreBooleanMap = {
    awake: false,
    labored_breathing: false,
    rearing: false,
    sniffing: false,
    grooming: false,
    dead: false,
  };
  switch (score) {
    case 0:
      return scoreBooleanMap;
    case 1:
      return { ...scoreBooleanMap, awake: true };
    case 2:
      return { ...scoreBooleanMap, awake: true, labored_breathing: true };
    case 3:
      return { ...scoreBooleanMap, awake: true, rearing: true };
    case 4:
      return { ...scoreBooleanMap, awake: true, sniffing: true };
    case 5:
      return { ...scoreBooleanMap, awake: true, grooming: true };
    case 6:
      return { ...scoreBooleanMap, dead: true };
    default:
      return undefined;
  }
}

export function TimeToFrameNumber(minutes: number, seconds: number, frame_rate: number) {
  const total_seconds = Number(minutes) * 60 + Number(seconds);
  return Math.round(total_seconds * frame_rate);
}
export function FrameNumberToTime(frame_num: number, frame_rate: number) {
  const minutes = Math.floor(frame_num / frame_rate / 60); // 60
  const seconds = Math.round((frame_num / frame_rate) - (minutes * 60));
  if (seconds === 60) {
    return { minutes: minutes + 1, seconds: 0 };
  }
  return { minutes, seconds };
}

export interface MotionFrameList {
  start_minute: number;
  start_second: number;
  end_minute: number;
  end_second: number;
}
/**
 * A single frame within a dataset.
 */
export interface AnnotationFrame {
  _id: string;
  dataset_id: string;
  study_type: string;
  validation_only: boolean;
  image_source: string;
  image_path: string;
  score: AnnotationScore;
  annotations: {
    regions: Annotation[];
    type: AnnotationListType;
  };
  reference: AnnotationFrameReference;
  status: AnnotationFrameStatus;
  inserted_by: string;
  confirmed_by: string;
  updated_by: string;
  updated_at: string;
  motion_prediction?: {
    predicted_class: RawMotionScoreValue;
    confidence: [number, number, number];
  };

  // Locally added
  file?: File;
}

export interface NewAnnotationFrame {
  _id?: string;
  dataset_id: string;
  study_type: string;
  image_source: string;
  image_path: string;
  score: AnnotationScore;
  annotations: {
    regions: Annotation[];
    type: AnnotationListType;
  };
  reference: NewAnnotationFrameReference;
  status: AnnotationFrameStatus;
  inserted_by: string;
  confirmed_by: string;
  updated_by: string;
  updated_at: string;
  motion_prediction?: {
    predicted_class: RawMotionScoreValue;
    confidence: [number, number, number];
  };

  // Locally added
  file?: File;
}

export type Dataset = AnnotationFrame[];
export type ReadonlyDataset = readonly AnnotationFrame[];

export interface SimpleFrameData {
  filename: string;
  path: string;
  index: number;
}

export type UserGroup = 'admins' | 'supervisors' | 'scorers' | 'annotators' | 'users' | 'userhighs';
export enum UserLevel {
  Admin = 0,
  Supervisor = 1,
  Scorer = 2,
  Annotator = 3,
  User = 4,
  Userhigh = 5
}

export interface User {
  _id: string;
  username: string;
  email: string;
  email_verified: boolean;
  user_level: UserLevel;
  user_group: UserGroup;
  institution: string;
  accessible_institutions: string[];
  last_password_at: string;
}

export interface Institution {
  id: string;
  name: string;
  description: string;
  director: string;
}

// Request Payloads

// This is for an admin to change other user passwords
export interface ResetUserPasswordPayload {
  username: string;
  password: string;
}

// This is for a user to change their own password
export interface ResetPasswordPayload {
  old_password: string;
  new_password: string;
}

export interface RegisterUserPayload extends ResetUserPasswordPayload {
  user_level: number;
  user_group: string;
  institution: string;
  accessible_institutions: string[];
  email: string;
}

// Type alias
export type UpdateUserPayload = Omit<RegisterUserPayload, 'username' | 'password' | 'institution'> & {
  email_verified: boolean;
};

export interface ChangeUserLevelPayload {
  username: string;
  user_level: number;
}

export interface ChangeUserInstitutionsPayload {
  username: string;
  accessible_institutions: string[];
}

export interface ScoreType {
  description: string;
  type: string;
  values: number[];
}

export interface AnnotationClassDefinition {
  class: string;
  type: string;
}

// These are somewhat hardcoded from the current list of score types
export type ScoreSet = number[];
export type ScoreRange = [number, number][];
export type ScoreClassValue = ScoreSet | ScoreRange;

export interface ScoreClassDefinition {
  class: string;
  type: string;
  value: ScoreClassValue;
}

export interface MotionScoreClassDefinition {
  score: number;
  text: string;
  color: string;
}

export interface SubjectTemplate {
  description: string;
  type: string;
  annotations: AnnotationClassDefinition[];
  scores: ScoreClassDefinition[];
}

export interface VideoUploadPolicy {
  allowed: boolean;
  disabled_from: string | null;
  timezone: string;
  replacement_url: string;
  message: string;
}

export interface ApplicationData {
  lists: {
    institutions: string[];
    annotation_types: {
      [type: string]: object;
    };
    score_types: {
      [type: string]: object;
    };
    user_types: string[];
    subject_types: string[];
    subject_sex: string[];
    study_types: string[];
  };

  subject_templates: {
    [template: string]: {
      [studyType: string]: SubjectTemplate;
    };
  };
}

// Video Evaluation
export interface EvaluateVideoPayload {
  video_filename: string;
  interval: number;
  overwrite: boolean;
  fau_model_id?: string;
  mgs_model_id?: string;
  motion_model_id?: string;
}

export interface EvaluateVideoDispatchResponse {
  task_id: string;
}

export interface VideoEvaluationResult {
  dataset_id: string;
  datetime: string;

  // CSV filename/size
  filename: string;
  size: number;

  // Zip filename/size
  zip_filename: string;
  zip_size: number;
}

export interface CheckResult {
  check: boolean;
  reason: string;
}

export interface VideoEvaluationStatus {
  status?: 'PENDING' | 'SUCCESS' | 'FAILURE' | 'REVOKED';

  // Progress is in percent
  current?: number;
  total?: number;
  progress?: number;

  result?: VideoEvaluationResult;
}

export type VideoType = 'user' | 'system';
export interface Task {
  // Will always be equal, redundant
  _id: string;
  task_id: string;

  name: 'batch_worker.evaluateVideo' | 'motion_worker.evaluateMotion';
  requested_at: string;
  requested_by: string;
  video_filename: string;
  status: VideoEvaluationStatus;
}

export type UserMetricsPeriod = 'mtd' | 'ytd' | 'from_inception';
export interface UserMetrics {
  total_users: number;

  // Jobs
  total_evaluation_jobs_submitted: number;
  evaluation_jobs_submitted_per_user: Record<string, number>;

  // Frames
  total_individual_frames_evaluated: number;
  individual_frames_evaluated_per_user: Record<string, number>;

  // Videos
  total_videos_uploaded: number;
  videos_uploaded_per_user: Record<string, number>;
  server_storage_space_used_by_each_user_in_GB: Record<string, number>;
}

export type UserMetricsGranularity = 'day' | 'month' | 'year';

export interface UserMetricsTimelinePoint {
  period: string;
  jobs: number;
  frames: number;
  videos: number;
  storage_gb: number;
}

export interface UserMetricsTimeline {
  granularity: UserMetricsGranularity;
  series: UserMetricsTimelinePoint[];
}

// vue-file-toolbar-menu
export interface Menu {
  is?: string | object;
  text?: string;
  html?: string;
  title?: string;
  icon?: string;
  emoji?: string;
  hotkey?: string;
  active?: boolean;
  disabled?: boolean;
  click?: Function;
  class?: string | object;
  id?: string;
  menu_width?: number;
  menu_height?: number;
  menu_class?: string;
  menu_id?: string;
  chevron?: boolean | string;
  menu?: Menu[];

  // Locally added, has no direct effect
  unimplemented?: boolean;
  disallowed?: UserLevel[];
}

export interface Notification {
  _id: string;
  type: string;
  text: string;
  created_at: string;
}

export interface PaginatedNotifications {
  total_number: number;
  total_pages: number;
  limit: number;
  page: number;
  notifications: Notification[];
}

export interface StatisticsOptions {
  num_samples: number;
  threshold_low: number; // def 2 => less than or equal to
  threshold_high: number; // def 5 => greater than or equal to
}

export interface StatisticsResult {
  result: object;
}
